import { Application } from '../../config'
import { compareStops, isWheelPicker } from '../../helpers'
import { IAvailability, IFavoriteResponse, IPaymentActions, IPaymentState, IReservation, IServiceInfo, IStop } from '../../models'
import { LiteralsService } from '../../services'
import { INewReservationState } from '../states'
import {
  BusStopsStepTypeKeys,
  ConfirmStepTypeKeys,
  DateStepTypeKeys,
  NewReservationActionTypes,
  RouteOptionStepTypeKeys
} from '../types'

export const initialNewReservationState: INewReservationState = {
  isEdit: false,
  pickedReservationId: undefined,
  serviceInfos: [],
  isFetchingServices: false,
  isSearching: false,
  nameStopSearched: '',
  stopsSearched: [],
  searchedType: undefined,
  coordinates: undefined,
  pickedService: undefined,
  pickedOriginBusStop: undefined,
  isFetchingDestinationBusStops: false,
  destinationBusStops: [],
  pickedDestinationBusStop: undefined,
  specificDate: false,
  pickedDate: new Date(),
  pickedSeats: 1,
  pickedPrmSeats: 0,
  isFetchingRouteOptions: false,
  routeOptions: [],
  pickedAvailability: undefined,
  isConfirmingReservation: false,
  isMarkerSelected: false,
  mapChangedCenter: null,
  paymentState: IPaymentState.PAYMENT_NOT_DO_IT,
  paymentAction: IPaymentActions.BOOK,
  isWheelPicker: true,
  isRoundTrip: false,
  returnPickedDate: new Date(),
  returnSpecificDate: false,
  weekDaysListReturn: [],
  showTripTypeOptions: false,
  isSingleStopModalOpen: false,
  isOrigin: true,
  isSearchingCoordsInMap: false,
  isMapCenterWithinOutlines: true,
  previewOriginStop: undefined,
  previewDestinationStop: undefined,
  cachedAddresses: [],
  stopAvailabilityTimes: [],
  isFetchingStopAvailabilityTimes: false
}

const { isAmb } = Application;

const CACHED_ADDRESSES_LIMIT = 50

function updateBusStops(
  state: INewReservationState,
  stop: IStop
): INewReservationState {
  if (!state.pickedOriginBusStop && !state.pickedDestinationBusStop) {
    return {
      ...state,
      pickedOriginBusStop: stop,
      coordinates: stop.location
    }
  } else if (
    state.pickedOriginBusStop &&
    !compareStops(state.pickedOriginBusStop, stop) &&
    !state.pickedDestinationBusStop
  ) {
    const { isRoundTrip, showTripTypeOptions } = checkIfIsRoundTripAndShowTripTypeOptions(state.serviceInfos, state.pickedOriginBusStop, stop)
    return {
      ...state,
      pickedDestinationBusStop: stop,
      pickedServiceInfo: stop.serviceInfo?.id,
      showTripTypeOptions,
      isRoundTrip
    }
  } else if (
    !!state.pickedDestinationBusStop &&
    compareStops(state.pickedDestinationBusStop, stop)
  ) {
    return {
      ...state,
      pickedDestinationBusStop: initialNewReservationState.pickedDestinationBusStop,
      showTripTypeOptions: false,
      singleStopSelected: undefined
    }
  }

  return {
    ...state,
    routeOptionsAndFailures: undefined,
    pickedService: initialNewReservationState.pickedService,
    pickedOriginBusStop: initialNewReservationState.pickedOriginBusStop,
    pickedDestinationBusStop: initialNewReservationState.pickedDestinationBusStop,
    pickedAvailability: undefined,
    pickedDate: new Date(),
    returnPickedAvailability: undefined,
    returnRouteOptionsAndFailures: undefined,
    returnPickedDate: new Date(),
    destinationBusStops: initialNewReservationState.destinationBusStops,
    pickedServiceInfo: undefined,
    showTripTypeOptions: false,
    singleStopSelected: undefined
  }
}

function reservationToReservationState(
  state: INewReservationState,
  reservation: IReservation
): INewReservationState {
  const pickedServiceInfo = state.serviceInfos.find((serviceInfo) => serviceInfo.id === reservation.serviceInfo.id)!
  return {
    ...initialNewReservationState,
    serviceInfos: state.serviceInfos,
    isEdit: true,
    pickedReservationId: reservation.id,
    pickedRelatedReservationId: reservation.relatedReservationId,
    pickedService: reservation.service.id,
    pickedServiceInfo: reservation.serviceInfo.id,
    pickedOriginBusStop: reservation.beginningStop,
    pickedDestinationBusStop: reservation.seats[0].exitStop,
    pickedSeats: reservation.seats.filter(seat => seat.prm === false).length,
    pickedPrmSeats: reservation.seats.filter(seat => seat.prm).length,
    pickedTown: reservation.townId,
    pickedDate: reservation.dateTime,
    returnPickedDate: reservation.returnDate,
    pickedAvailability: reservation.availabilityId ? {id: reservation.availabilityId} as IAvailability : undefined,
    returnPickedAvailability: reservation.returnAvailabilityId ? {id: reservation.returnAvailabilityId} as IAvailability : undefined,
    isRoundTrip: pickedServiceInfo?.roundTrips || (reservation.relatedReservationId !== null)
  }
}

function favoriteToReservationState(
  state: INewReservationState,
  favorite: IFavoriteResponse
): INewReservationState {
  const pickedServiceInfo = state.serviceInfos.find(serviceInfo => serviceInfo.id === favorite.serviceInfoId)
  return {
    ...initialNewReservationState,
    serviceInfos: state.serviceInfos,
    pickedService: favorite.service.id,
    pickedOriginBusStop: {
      ...favorite.inStop.properties,
      location: favorite.inStop,
      serviceInfo: pickedServiceInfo
    } as any,
    pickedDestinationBusStop: {
      ...favorite.exitStop.properties,
      location: favorite.exitStop,
      serviceInfo: pickedServiceInfo
    } as any,
    isWheelPicker: state.isWheelPicker
  }
}

function updateServices(
  state: INewReservationState,
  serviceInfos: IServiceInfo[]
): INewReservationState {
  const { isRoundTrip, showTripTypeOptions } = checkIfIsRoundTripAndShowTripTypeOptions(serviceInfos, state.pickedOriginBusStop, state.pickedDestinationBusStop)
  let pickedOriginBusStop = state.pickedOriginBusStop
  let pickedDestinationBusStop = state.pickedDestinationBusStop
  if (state.pickedReservationId) {
    const pickedServiceInfo = serviceInfos.find((serviceInfo) => serviceInfo.id === state.pickedServiceInfo)!
    pickedOriginBusStop = {
      ...pickedOriginBusStop!,
      serviceInfo: pickedServiceInfo
    }
    pickedDestinationBusStop = {
      ...pickedDestinationBusStop!,
      serviceInfo: pickedServiceInfo
    }
  }
  return {
    ...state,
    serviceInfos: serviceInfos.map((s) => {
      return {
        ...s,
        selected: s.selected !== undefined ? s.selected : true,
        stops: s.stops.map((st) => {
          if (st.id > 0) return {...st, name: `${st.name} (${LiteralsService.get('finalStopTag')})`}
          return st
        })}
    }),
    isWheelPicker: isWheelPicker(serviceInfos),
    isRoundTrip: isRoundTrip || state.isRoundTrip,
    showTripTypeOptions,
    pickedOriginBusStop,
    pickedDestinationBusStop
  }
}

function checkIfIsRoundTripAndShowTripTypeOptions(serviceInfos: IServiceInfo[], origin?: IStop, destination?: IStop) {
  const hasRoundTripServiceInfos = serviceInfos.some((service) => service.roundTrips);
  console.log("has round trips SI?", hasRoundTripServiceInfos)
  if (origin && destination && !isAmb) {
    const originServices = serviceInfos.filter((service) => service.stops.some((stop) => stop.id === origin.id))
    const destinationServices = serviceInfos.filter((service) => service.stops.some((stop) => stop.id === destination.id))
    const commonServices = originServices.filter((originService) => destinationServices.includes(originService))
    if (commonServices.length === 1) {
      return { isRoundTrip: commonServices[0].roundTrips, showTripTypeOptions: hasRoundTripServiceInfos ? false : true }
    } else if (commonServices.length > 0) {
      return { isRoundTrip: true, showTripTypeOptions: true }
    } else {
      return { isRoundTrip: false, showTripTypeOptions: hasRoundTripServiceInfos ? false : true }
    }
  } else if ((origin && !destination) || (!origin && destination)) {  // single stop
    const singleStop = origin! || destination!
    return { isRoundTrip: singleStop.serviceInfo.roundTrips, showTripTypeOptions: true }
  } else {
    return { isRoundTrip: hasRoundTripServiceInfos, showTripTypeOptions: false }
  }
}

function getSelected(s: IServiceInfo, serviceInfo: IServiceInfo, selected: boolean): boolean {
  if (s.id === serviceInfo.id) return selected
  else return s.selected
}

function setServices(
  state: INewReservationState,
  payload: any
): INewReservationState {
  return {
    ...state,
    serviceInfos: state.serviceInfos.map((s) => {
      return {
        ...s,
        selected: getSelected(s, payload.serviceInfo, payload.selected)
    }})
  }
}

const newReservation = (
  state: INewReservationState = initialNewReservationState,
  action: NewReservationActionTypes
): INewReservationState => {
  switch (action.type) {
    case BusStopsStepTypeKeys.FETCHING_SERVICES:
      return { ...state, isFetchingServices: true }
    case BusStopsStepTypeKeys.FETCH_SERVICES_SUCCESSFUL:
      return {
        ...updateServices(state, Array.isArray(action.payload) ? [...action.payload] : [action.payload]),
        isFetchingServices: false
      }
    case BusStopsStepTypeKeys.SET_IS_SEARCHING: {
      return { ...state, isSearching: action.payload }
    }
    case BusStopsStepTypeKeys.SEARCH_STOPS: {
      return { ...state, nameStopSearched: action.payload }
    }
    case BusStopsStepTypeKeys.SET_SEARCH_STOPS: {
      return { ...state, stopsSearched: action.payload }
    }
    case BusStopsStepTypeKeys.SET_SEARCHED_RESULT: {
      return {
        ...state,
        searchedType: undefined,
        nameStopSearched: action.payload.name,
        coordinates: undefined
      }
    }
    case BusStopsStepTypeKeys.SET_BUS_STOP:
      return {
        ...updateBusStops(state, action.payload.stop),
        isMarkerSelected: false
      }
    case BusStopsStepTypeKeys.FETCHING_DESTINATION_BUS_STOPS:
      return {
        ...state,
        isFetchingDestinationBusStops: true
      }
    case BusStopsStepTypeKeys.FETCH_DESTINATION_BUS_STOPS_SUCCESSFUL:
      return {
        ...state,
        isFetchingDestinationBusStops: false,
        destinationBusStops: action.payload
      }
    case DateStepTypeKeys.SET_TYPE_SPECIFIC_DATE_FLAG:
      return {
        ...state,
        specificDate: action.payload
      }
    case DateStepTypeKeys.SET_PICKED_DATE:
      return {
        ...state,
        pickedDate: action.payload
      }
    case DateStepTypeKeys.SET_PICKED_SEATS:
      return {
        ...state,
        pickedSeats: action.payload.seats,
        pickedPrmSeats: action.payload.prmSeats
      }
    case RouteOptionStepTypeKeys.FETCHING_ROUTE_OPTIONS:
      return {
        ...state,
        isFetchingRouteOptions: true
      }
    case RouteOptionStepTypeKeys.FETCHING_ROUTE_OPTIONS_ECORRIDOR:
      return {
        ...state,
        isFetchingRouteOptions: true
      }
    case RouteOptionStepTypeKeys.FETCH_ROUTE_OPTIONS_SUCCESSFUL:
      if (state.pickedAvailability) {
        const updatedAvailability = action.payload.availabilities && action.payload.availabilities.find((availability: any) => availability.id === state.pickedAvailability?.id)
        if (updatedAvailability) state.pickedAvailability = updatedAvailability
        else state.pickedAvailability = undefined
      }
      return {
        ...state,
        isFetchingRouteOptions: false,
        routeOptionsAndFailures: action.payload
      }
    case RouteOptionStepTypeKeys.FETCH_ROUTE_OPTIONS_SUCCESSFUL_ECORRIDOR:
      return {
        ...state,
        isFetchingRouteOptions: false,
        routeOptions: action.payload
      }
    case RouteOptionStepTypeKeys.SET_AVAILABILITY:
      return {
        ...state,
        pickedAvailability: action.payload
      }
    case BusStopsStepTypeKeys.RESET_NEW_RESERVATION_FIELDS:
      return {
        ...initialNewReservationState,
        serviceInfos: state.serviceInfos,
        isWheelPicker: isWheelPicker(state.serviceInfos),
        showTripTypeOptions: false
      }
    case BusStopsStepTypeKeys.SET_NEW_RESERVATION_FIELDS:
      return reservationToReservationState(state, action.payload)
    case BusStopsStepTypeKeys.SET_FAVORITE_NEW_RESERVATION_FIELDS:
      return favoriteToReservationState(state, action.payload)
    case ConfirmStepTypeKeys.CONFIRMING_NEW_RESERVATION:
      return {
        ...state,
        isConfirmingReservation: true
      }
    case ConfirmStepTypeKeys.CONFIRM_NEW_RESERVATION:
      return {
        ...state,
        isConfirmingReservation: true,
        paymentState: IPaymentState.DOING_PAYMENT,
        paymentAction: action.payload.payOption,
        activePassId: action.payload.activePassId
      }
    case ConfirmStepTypeKeys.CONFIRMING_NEW_RESERVATION:
      return {
        ...state,
        isConfirmingReservation: true,
        paymentState: IPaymentState.DOING_PAYMENT
      }
    case ConfirmStepTypeKeys.CONFIRM_NEW_RESERVATION_SUCCESSFUL:
      return {
        ...state,
        isConfirmingReservation: false,
        userCreated: action.payload
      }
    case ConfirmStepTypeKeys.CONFIRM_NEW_RESERVATION_FAILED:
      return {
        ...state,
        isConfirmingReservation: false,
        paymentState: IPaymentState.PAYMENT_FAILED
      }
    case BusStopsStepTypeKeys.SET_SELECT_MARKER_FLAG:
      return { ...state, isMarkerSelected: action.payload }
    case BusStopsStepTypeKeys.SET_MAP_CHANGED_CENTER:
      return {
        ...state,
        mapChangedCenter: action.payload
      }
    case BusStopsStepTypeKeys.RESET_MAP_CHANGED_CENTER:
      return {
        ...state,
        mapChangedCenter: null
      }
    case BusStopsStepTypeKeys.SET_SERVICE_SELECTED:
      return setServices(state, action.payload)
    case BusStopsStepTypeKeys.SET_USER_LOCATION:
      return {
        ...state,
        userLocation: action.payload,
      }
    case BusStopsStepTypeKeys.SET_ROUND_TRIP:
      return {
        ...state,
        isRoundTrip: action.payload
      }
    case BusStopsStepTypeKeys.SET_SHOW_TRIP_TYPE_OPTIONS:
      return {
        ...state,
        showTripTypeOptions: action.payload
      }

    case BusStopsStepTypeKeys.SET_SINGLE_BUS_STOP_DIRECTION:
      let originStop = action.payload.isOrigin ? state.singleStopSelected : undefined
      let destinationStop = action.payload.isOrigin ? undefined : state.singleStopSelected

      if (action.payload.stop) {
        if (action.payload.isOrigin) originStop = action.payload.stop
        else destinationStop = action.payload.stop
      }
      return {
        ...state,
        pickedOriginBusStop: originStop,
        pickedDestinationBusStop: destinationStop,
        isOrigin: action.payload.isOrigin,
        isRoundTrip: checkIfIsRoundTripAndShowTripTypeOptions(state.serviceInfos, state.pickedOriginBusStop, state.pickedDestinationBusStop).isRoundTrip
      }
    case BusStopsStepTypeKeys.SHOW_SINGLE_STOP_MODAL:
      return {
        ...state,
        isSingleStopModalOpen: action.payload.isSingleStopModalOpen,
        pickedOriginBusStop: action.payload.isSingleStopModalOpen ? action.payload.stop : state.pickedOriginBusStop,
        singleStopSelected: action.payload.stop ?? state.singleStopSelected,
        isRoundTrip: action.payload.stop?.serviceInfo?.roundTrips ?? state.isRoundTrip,
        showTripTypeOptions: action.payload.stop ? checkIfIsRoundTripAndShowTripTypeOptions(state.serviceInfos, action.payload.stop)?.showTripTypeOptions : state.showTripTypeOptions
      }
    case BusStopsStepTypeKeys.RESET_SINGLE_BUS_STOP:
      return {
        ...state,
        pickedOriginBusStop: undefined,
        pickedDestinationBusStop: undefined,
        pickedAvailability: undefined,
        returnPickedAvailability: undefined,
        pickedDate: new Date(),
        returnPickedDate: new Date(),
        weekDaysListReturn: [],
        singleStopSelected: undefined,
        isOrigin: true,
        showTripTypeOptions: false,
        previewOriginStop: undefined,
        previewDestinationStop: undefined
      }
    case BusStopsStepTypeKeys.SET_IS_SEARCHING_COORDS_IN_MAP:
      return {
        ...state,
        isSearchingCoordsInMap: action.payload
      }
    case BusStopsStepTypeKeys.SET_IS_MAP_CENTER_WITHIN_OUTLINES:
      return {
        ...state,
        isMapCenterWithinOutlines: action.payload
      }
    case BusStopsStepTypeKeys.SET_PREVIEW_ORIGIN_STOP:
      return {
        ...state,
        previewOriginStop: action.payload
      }
    case BusStopsStepTypeKeys.SET_PREVIEW_DESTINATION_STOP:
      return {
        ...state,
        previewDestinationStop: action.payload
      }
    case BusStopsStepTypeKeys.SET_CACHED_ADDRESSES:
      return {
        ...state,
        cachedAddresses: action.payload
      }
    case BusStopsStepTypeKeys.ADD_CACHED_ADDRESS:
      return {
        ...state,
        // Most recent first, without repeating the same address.
        cachedAddresses: [
          action.payload,
          ...state.cachedAddresses.filter(address => address.id !== action.payload.id)
        ].slice(0, CACHED_ADDRESSES_LIMIT)
      }
    case BusStopsStepTypeKeys.SET_IS_FETCHING_STOP_AVAILABILITY_TIMES:
      return {
        ...state,
        isFetchingStopAvailabilityTimes: action.payload
      }
    case BusStopsStepTypeKeys.SET_STOP_AVAILABILITY_TIMES:
      return {
        ...state,
        stopAvailabilityTimes: action.payload,
        isFetchingStopAvailabilityTimes: false
      }
    case BusStopsStepTypeKeys.RESET_STOP_AVAILABILITY_TIMES:
      return {
        ...state,
        stopAvailabilityTimes: [],
        isFetchingStopAvailabilityTimes: false
      }
    case DateStepTypeKeys.SET_RETURN_TYPE_SPECIFIC_DATE_FLAG:
      return {
        ...state,
        returnSpecificDate: action.payload
      }
    case DateStepTypeKeys.SET_RETURN_PICKED_DATE:
      return {
        ...state,
        returnPickedDate: action.payload
      }
    case DateStepTypeKeys.SET_WEEK_DAYS_LIST_RETURN:
      return {
        ...state,
        weekDaysListReturn: action.payload
      }
    case RouteOptionStepTypeKeys.FETCHING_RETURN_ROUTE_OPTIONS:
      return {
        ...state,
        isFetchingReturnRouteOptions: true
      }
    case RouteOptionStepTypeKeys.FETCH_RETURN_ROUTE_OPTIONS_SUCCESSFUL:
      if (state.returnPickedAvailability) {
        const updatedAvailability = action.payload.availabilities.find((availability: any) => availability.id === state.returnPickedAvailability?.id)
        if (updatedAvailability) state.returnPickedAvailability = updatedAvailability
        else state.returnPickedAvailability = undefined
      }
      return {
        ...state,
        isFetchingReturnRouteOptions: false,
        returnRouteOptionsAndFailures: action.payload
      }
    case RouteOptionStepTypeKeys.SET_RETURN_AVAILABILITY:
      return {
        ...state,
        returnPickedAvailability: action.payload
      }
    default:
      return state
  }
}

export default newReservation
